import { useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { readCsrfCookie } from "../auth/csrfCookie";
import { useReferences } from "./useReferences";

function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "something went wrong";
}

/**
 * Registers the real, tenant-scoped references a published flow's own
 * validation checks against (store.TenantReferenceResolver): assets for a
 * `play` node, voices for a `tts` node. There is no real asset scanning or
 * TTS rendering behind these yet — registering an id IS what makes it
 * "ready" for now (docs/PROGRESS.md) — this only proves the identifier is a
 * real, deliberate tenant choice, which is what publish's own validation
 * checks for.
 */
export function ReferencesPanel() {
  const { assets, voices, loading, error, registerAsset, registerVoice } = useReferences();
  const [assetId, setAssetId] = useState("");
  const [voice, setVoice] = useState("");
  const [language, setLanguage] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [submittingAsset, setSubmittingAsset] = useState(false);
  const [submittingVoice, setSubmittingVoice] = useState(false);

  const submitAsset = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmittingAsset(true);
    setLocalError(null);
    try {
      const csrfToken = readCsrfCookie(document.cookie);
      await api.registerAsset(csrfToken, assetId);
      setAssetId("");
      await registerAsset();
    } catch (err) {
      setLocalError(describeError(err));
    } finally {
      setSubmittingAsset(false);
    }
  };

  const submitVoice = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmittingVoice(true);
    setLocalError(null);
    try {
      const csrfToken = readCsrfCookie(document.cookie);
      await api.registerVoice(csrfToken, voice, language);
      setVoice("");
      setLanguage("");
      await registerVoice();
    } catch (err) {
      setLocalError(describeError(err));
    } finally {
      setSubmittingVoice(false);
    }
  };

  return (
    <section aria-labelledby="references-heading" className="card">
      <h2 id="references-heading">Registered references</h2>
      <p className="card__note">
        Publishing checks every asset/voice a flow uses against what's registered here for real —
        there is no scanning or TTS rendering behind them yet, but an unregistered reference is
        refused, never silently accepted.
      </p>

      <form className="invites__form" onSubmit={(event) => void submitAsset(event)}>
        <label className="field">
          <span>Asset ID</span>
          <input type="text" value={assetId} onChange={(event) => setAssetId(event.target.value)} required />
        </label>
        <button type="submit" disabled={submittingAsset}>
          Register asset
        </button>
      </form>
      <form className="invites__form" onSubmit={(event) => void submitVoice(event)}>
        <label className="field">
          <span>Voice</span>
          <input type="text" value={voice} onChange={(event) => setVoice(event.target.value)} required />
        </label>
        <label className="field">
          <span>Language</span>
          <input type="text" value={language} onChange={(event) => setLanguage(event.target.value)} required />
        </label>
        <button type="submit" disabled={submittingVoice}>
          Register voice
        </button>
      </form>

      {(error ?? localError) ? <p className="card__error">{error ?? localError}</p> : null}

      {loading ? (
        <p className="card__note">Loading…</p>
      ) : (
        <ul className="invites__list">
          {assets.map((a) => (
            <li key={"asset:" + a.asset_id} className="invites__item">
              <span>asset · {a.asset_id}</span>
            </li>
          ))}
          {voices.map((v) => (
            <li key={"voice:" + v.voice + ":" + v.language} className="invites__item">
              <span>
                voice · {v.voice} ({v.language})
              </span>
            </li>
          ))}
          {assets.length === 0 && voices.length === 0 ? (
            <li className="invites__item">
              <span>Nothing registered yet.</span>
            </li>
          ) : null}
        </ul>
      )}
    </section>
  );
}
